import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import PasswordInput from '../components/PasswordInput';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter'; 
import { validatePassword } from '../utils/passwordValidation'; 
import {
  Button,
  FormControl,
  FormLabel,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  Progress,
} from '@chakra-ui/react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast({
        title: 'Token manquant',
        description: 'Le lien de réinitialisation est invalide',
        status: 'error',
        duration: 5000,
      });
      navigate('/forgot-password');
    } else {
      // Optionnel: vérifier la structure du token
      setToken(tokenParam);
      // Vous pourriez aussi appeler une API pour vérifier la validité du token
      // validateToken(tokenParam);
    }
  }, [searchParams, navigate, toast]);

   const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation des mots de passe
    if (password !== confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    // Validation de force du mot de passe
    const validation = validatePassword(password);
    if (!validation.isValid) {
      toast({
        title: 'Mot de passe trop faible',
        description: validation.errors.join(', '),
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        toast({
          title: 'Succès',
          description: 'Votre mot de passe a été réinitialisé avec succès',
          status: 'success',
          duration: 5000,
        });
        
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Gestion spécifique des erreurs HTTP
        let errorMessage = 'Une erreur est survenue';
        
        switch (response.status) {
          case 400:
            errorMessage = data.error || 'Requête invalide';
            break;
          case 401:
            errorMessage = 'Token invalide ou expiré';
            break;
          case 404:
            errorMessage = 'Utilisateur non trouvé';
            break;
          case 500:
            errorMessage = 'Erreur serveur, veuillez réessayer plus tard';
            break;
        }
        
        toast({
          title: 'Erreur',
          description: data.error || 'Une erreur est survenu',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur de connexion',
        description: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Heading size="lg" textAlign="center" color="green.500">
                ✓ Mot de passe réinitialisé
              </Heading>
              <Text textAlign="center">
                Votre mot de passe a été modifié avec succès.
              </Text>
              <Text textAlign="center" color="gray.600">
                Redirection vers la page de connexion...
              </Text>
              <Button
                colorScheme="blue"
                onClick={() => navigate('/login')}
              >
                Se connecter maintenant
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <Heading size="lg" textAlign="center" color="blue.500">
                Réinitialiser le mot de passe
              </Heading>
              <Text textAlign="center" color="gray.600">
                Choisissez un nouveau mot de passe sécurisé.
              </Text>

              <FormControl isRequired>
                <FormLabel>Nouveau mot de passe</FormLabel>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                />
                {/* ⭐ REMPLACÉ par PasswordStrengthMeter */}
                <PasswordStrengthMeter password={password} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirmer le mot de passe</FormLabel>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retapez le mot de passe"
                />
              </FormControl>

              {password && confirmPassword && password !== confirmPassword && (
                <Alert status="error" borderRadius="md" size="sm">
                  <AlertIcon />
                  Les mots de passe ne correspondent pas
                </Alert>
              )}

              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isLoading}
                loadingText="Réinitialisation..."
                isDisabled={
                  !password || 
                  !confirmPassword || 
                  password !== confirmPassword ||
                  validatePassword(password).strength < 40 // ⭐ Désactiver si trop faible
                }
              >
                Réinitialiser le mot de passe
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
              >
                Retour à la connexion
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Container>
  );
}
